export type TimeFormat = "12h" | "24h";

/**
 * Parses what people type into Clockify's start/end boxes into minutes after midnight.
 * Accepts "9", "930", "0930", "9:30", "9.30", "9:30pm", "9p", "21:30", "12am".
 * Returns null for anything that is not a valid wall-clock time.
 */
export function parseTime(text: string): number | null {
  const s = text.trim().toLowerCase().replace(/\s+/g, "");
  const m = /^(\d{1,4})(?:[:.](\d{1,2}))?(a|p|am|pm)?$/.exec(s);
  if (!m) return null;
  const [, digits = "", minPart, suffix] = m;
  let hours: number;
  let minutes: number;
  if (minPart !== undefined) {
    if (digits.length > 2 || minPart.length !== 2) return null;
    hours = Number(digits);
    minutes = Number(minPart);
  } else if (digits.length <= 2) {
    hours = Number(digits);
    minutes = 0;
  } else {
    // "930" -> 9:30 and "1230" -> 12:30: the last two digits are always minutes.
    hours = Number(digits.slice(0, -2));
    minutes = Number(digits.slice(-2));
  }
  if (minutes > 59) return null;
  if (suffix) {
    if (hours < 1 || hours > 12) return null;
    const pm = suffix.startsWith("p");
    if (hours === 12) hours = pm ? 12 : 0;
    else if (pm) hours += 12;
  } else if (hours > 23) {
    return null;
  }
  return hours * 60 + minutes;
}

/** Clockify renders `09:30AM` (zero-padded, no space; Preferences shows `09:00AM`) or `09:30`. */
export function formatTime(minutes: number, format: TimeFormat): string {
  const total = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const m = total % 60;
  const mm = String(m).padStart(2, "0");
  if (format === "24h") return `${String(h).padStart(2, "0")}:${mm}`;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${mm}${h < 12 ? "AM" : "PM"}`;
}
